import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldAlert, RefreshCw, AlertTriangle, Clock, CheckCircle2, Loader2, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Building2, label: 'Admin Dashboard', href: '/admin/dashboard' },
  { icon: ShieldAlert, label: 'Credentialing', active: true },
];

export default function AdminCredentialingLapse() {
  const { getAuthHeader } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [lastSweep, setLastSweep] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/credentialing/lapse-report`, getAuthHeader());
      setReport(data);
    } catch (e) {
      toast.error('Could not load credentialing report');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await axios.post(`${API}/admin/credentialing/auto-revoke-sweep`, {}, getAuthHeader());
      setLastSweep(data);
      const r = data.newly_revoked || 0;
      const w = data.warned_expiring || 0;
      toast.success(`Sweep done — ${r} revoked · ${w} warned`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Credentialing — lapse report"
      subtitle="Active clinicians whose license / NPI / background check is expiring or already lapsed. The auto-revoke sweep cancels future assignments + notifies hospitals."
      testId="admin-credentialing-page"
    >
      <div className="cn-card p-5 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="lapse-report-controls">
        <div>
          <p className="text-sm text-cn-text-muted">Sweep cadence: nightly. Manual sweep below.</p>
          {lastSweep && (
            <p className="text-xs text-cn-text-muted mt-1" data-testid="lapse-report-last-sweep">
              Last sweep: scanned {lastSweep.scanned} · {lastSweep.newly_revoked} revoked · {lastSweep.warned_expiring} warned
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} disabled={loading} className="cn-btn cn-btn-ghost !py-2" data-testid="lapse-report-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={runSweep} disabled={sweeping} className="cn-btn cn-btn-primary !py-2" data-testid="lapse-report-sweep">
            {sweeping ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
            Run sweep now
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>
      ) : !report ? (
        <p className="text-sm text-cn-text-muted">No data.</p>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="cn-card p-5" data-testid="lapse-tile-lapsed">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Lapsed</p>
                  <p className="font-display font-bold text-3xl text-cn-text mt-1">{report.lapsed_count}</p>
                </div>
                <span className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center"><AlertTriangle className="w-6 h-6" /></span>
              </div>
              <p className="text-xs text-cn-text-muted mt-1">New placements blocked; future visits cancelled on next sweep.</p>
            </div>
            <div className="cn-card p-5" data-testid="lapse-tile-expiring">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Expiring soon</p>
                  <p className="font-display font-bold text-3xl text-cn-text mt-1">{report.expiring_count}</p>
                </div>
                <span className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><Clock className="w-6 h-6" /></span>
              </div>
              <p className="text-xs text-cn-text-muted mt-1">Expiring within 14 days. Clinicians notified in-app.</p>
            </div>
          </div>

          <div className="cn-card p-5 mb-6" data-testid="lapse-list-lapsed">
            <h4 className="font-display font-semibold text-base text-cn-text mb-3">Lapsed clinicians ({report.lapsed_count})</h4>
            {(report.lapsed || []).length === 0 ? (
              <p className="text-sm text-cn-text-muted inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> No lapsed credentials. Compliance posture is clean.</p>
            ) : (
              <div className="divide-y divide-cn-border">
                {report.lapsed.map((r) => (
                  <div key={r.user_id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`lapse-row-${r.user_id}`}>
                    <div>
                      <p className="font-semibold text-cn-text">{r.name}</p>
                      <p className="text-xs text-cn-text-muted">Lapsed credentials: {(r.details?.lapsed_credentials || []).join(', ') || '—'}</p>
                    </div>
                    <span className="text-[11px] px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 font-semibold">
                      {r.verification_status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="cn-card p-5" data-testid="lapse-list-expiring">
            <h4 className="font-display font-semibold text-base text-cn-text mb-3">Expiring soon ({report.expiring_count})</h4>
            {(report.expiring || []).length === 0 ? (
              <p className="text-sm text-cn-text-muted">Nothing expiring in the next 14 days.</p>
            ) : (
              <div className="divide-y divide-cn-border">
                {report.expiring.map((r) => (
                  <div key={r.user_id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`expiring-row-${r.user_id}`}>
                    <div>
                      <p className="font-semibold text-cn-text">{r.name}</p>
                      <p className="text-xs text-cn-text-muted">
                        {(r.details?.expiring_credentials || []).map((e) => `${e.credential} (${(e.expires_at || '').slice(0, 10)})`).join(', ') || '—'}
                      </p>
                    </div>
                    <span className="text-[11px] px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">Expiring</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </DashboardShell>
  );
}
